import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { userBootstrapApi } from "../api/bootstrap";
import { useAuth } from "./AuthContext";
import {
  canAccessSurfaceForRoles,
  canEngineerOwnerGroupForMemberships,
  canOperateOwnerGroupForMemberships,
  canViewAnyDeviceOperationalStatusForMemberships,
  canViewDeviceOperationalStatusForMemberships,
  getSurfaceAccessForRoles,
  hasRoleInGroupForMemberships,
  type AccessProfile,
  type AccessDeviceTier,
  type GlobalRole,
  type GroupMembershipAccess,
  type GroupRole,
  type SurfaceKey,
} from "../authz/surfaceAccess";
import { useDeviceTier } from "../hooks/useDeviceTier";
import { getAccessProfileErrorMessage } from "../utils/accessProfileErrors";

interface GroupMembershipRecord {
  group_id: number;
  role: GroupRole;
  status: string;
}

interface UserGlobalRoleRecord {
  role: GlobalRole;
  status: string;
}

const SurfaceAccessContext = createContext<AccessProfile | undefined>(undefined);

const getAuthUserSub = (user: any | null): string | null =>
  user?.attributes?.sub || user?.userId || user?.username || null;

export const SurfaceAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const deviceTier = useDeviceTier() as AccessDeviceTier;
  const userSub = getAuthUserSub(user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<GroupMembershipAccess[]>([]);
  const [isSystemUser, setIsSystemUser] = useState(false);

  useEffect(() => {
    let ignore = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const fetchAccessProfile = async () => {
      if (!isAuthenticated || !userSub) {
        setMemberships([]);
        setIsSystemUser(false);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const bootstrap = await userBootstrapApi.get();
        if (bootstrap.user_sub && bootstrap.user_sub !== userSub) {
          userBootstrapApi.clear();
          throw new Error(`Bootstrap profile user mismatch: ${bootstrap.user_sub} !== ${userSub}`);
        }
        const membershipData = bootstrap.group_memberships || [];
        const globalRoleData = bootstrap.user_global_roles || [];

        if (ignore) return;

        const activeMemberships = (Array.isArray(membershipData) ? membershipData : [])
          .filter((membership: GroupMembershipRecord) => membership.status === "active")
          .map((membership: GroupMembershipRecord) => ({
            group_id: membership.group_id,
            role: membership.role,
            status: membership.status,
          }));

        const hasSystemUserRole = (Array.isArray(globalRoleData) ? globalRoleData : []).some(
          (role: UserGlobalRoleRecord) => role.role === "system_user" && role.status === "active"
        );

        setMemberships(activeMemberships);
        setIsSystemUser(hasSystemUserRole);
      } catch (err) {
        if (ignore) return;
        console.error("Failed to load surface access profile:", err);
        setError(getAccessProfileErrorMessage(err));
        retryTimer = setTimeout(() => {
          void fetchAccessProfile();
        }, 5000);
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    void fetchAccessProfile();

    return () => {
      ignore = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isAuthenticated, userSub]);

  const activeRoles = useMemo(
    () => new Set(memberships.map((membership) => membership.role)),
    [memberships]
  );

  const canAccessSurface = useCallback(
    (surface: SurfaceKey) => canAccessSurfaceForRoles(surface, activeRoles, isSystemUser, deviceTier),
    [activeRoles, deviceTier, isSystemUser]
  );

  const getSurfaceAccess = useCallback(
    (surface: SurfaceKey) => getSurfaceAccessForRoles(surface, activeRoles, isSystemUser, deviceTier),
    [activeRoles, deviceTier, isSystemUser]
  );

  const hasRoleInGroup = useCallback(
    (groupId: number | string | null | undefined, role: GroupRole) =>
      hasRoleInGroupForMemberships(memberships, groupId, role),
    [memberships]
  );

  const canEngineerOwnerGroup = useCallback(
    (groupId: number | string | null | undefined) =>
      canEngineerOwnerGroupForMemberships(memberships, isSystemUser, groupId),
    [isSystemUser, memberships]
  );

  const canOperateOwnerGroup = useCallback(
    (groupId: number | string | null | undefined) =>
      canOperateOwnerGroupForMemberships(memberships, isSystemUser, groupId),
    [isSystemUser, memberships]
  );

  const canViewDeviceOperationalStatus = useCallback(
    (groupId: number | string | null | undefined) =>
      canViewDeviceOperationalStatusForMemberships(memberships, isSystemUser, groupId),
    [isSystemUser, memberships]
  );

  const canViewAnyDeviceOperationalStatus = useCallback(
    () => canViewAnyDeviceOperationalStatusForMemberships(memberships, isSystemUser),
    [isSystemUser, memberships]
  );

  const value = useMemo<AccessProfile>(
    () => ({
      isLoading,
      error,
      userSub,
      deviceTier,
      memberships,
      activeRoles,
      isSystemUser,
      canAccessSurface,
      getSurfaceAccess,
      hasRoleInGroup,
      canEngineerOwnerGroup,
      canOperateOwnerGroup,
      canViewDeviceOperationalStatus,
      canViewAnyDeviceOperationalStatus,
    }),
    [activeRoles, canAccessSurface, canEngineerOwnerGroup, canOperateOwnerGroup, canViewAnyDeviceOperationalStatus, canViewDeviceOperationalStatus, deviceTier, error, getSurfaceAccess, hasRoleInGroup, isLoading, isSystemUser, memberships, userSub]
  );

  return <SurfaceAccessContext.Provider value={value}>{children}</SurfaceAccessContext.Provider>;
};

export const useSurfaceAccess = () => {
  const context = useContext(SurfaceAccessContext);
  if (!context) {
    throw new Error("useSurfaceAccess must be used within a SurfaceAccessProvider");
  }
  return context;
};
